// One SSE connection per browser, shared by every tab (plan §8).
//  - Tabs elect a leader with the Web Locks API (`wd-sse`, exclusive). Only the leader opens /api/events.
//  - The leader relays every event over a BroadcastChannel; followers apply the same events.
//  - A tab that is closed or crashes releases its lock, and the next queued tab takes over.
//  - A leader that is frozen (bfcache, discarded timers) keeps its lock but stops relaying. The server
//    sends a heartbeat every 25 s, so a visible follower that hears nothing for 60 s steals the lock.
//  - The new leader resumes with Last-Event-ID (as ?lastEventId=); events already applied are dropped by id.
//  - Without Web Locks (old browsers) each tab connects on its own: slower, never wrong.
export type HubEventName = 'update' | 'ready' | 'reset' | 'heartbeat' | 'closed';
export interface HubEvent {
  name: HubEventName;
  id?: number;
  data: any;
}
export interface HubDeps {
  EventSource: typeof EventSource;
  BroadcastChannel: typeof BroadcastChannel | undefined;
  locks: LockManager | undefined;
  now: () => number;
  visible: () => boolean;
  /** Heartbeat silence after which a visible follower takes over. */
  staleMs: number;
  /** How often followers check for a stale leader. */
  checkMs: number;
}
export const LOCK_NAME = 'wd-sse';
const CHANNEL = 'wd-events';

export const browserDeps = (): HubDeps => ({
  EventSource: globalThis.EventSource,
  BroadcastChannel: globalThis.BroadcastChannel,
  locks: globalThis.navigator?.locks,
  now: () => Date.now(),
  visible: () => document.visibilityState === 'visible',
  staleMs: 60_000,
  checkMs: 10_000,
});

type Relay = { owner: string; tag?: string | null; name: HubEventName | 'hello'; id?: number; data: any };

/** Outcome of the identity probe run after an ambiguous stream error (lib/api.ts probeIdentity). */
export type ProbeOutcome = 'changed' | 'unauthorized' | 'transient' | 'ok';
/**
 * Stale-tab fence (§2h). With a tag, the stream connects with ?st=<tag>, applies and relays nothing until a
 * `ready` arrives, and treats a `ready` with another tag as a confirmed identity change. Relays carry the
 * tag and followers drop foreign ones. An error before any `ready` keeps the backoff and runs one probe.
 */
export type HubFence = {
  tag?: string | null;
  probe?: (tag: string | null) => Promise<ProbeOutcome>;
  /** Confirmed change: the caller reloads (or, when frozen, re-shows its dialog). */
  onChanged?: () => void;
  /** The probe got 401: the caller freezes (acting) or re-checks the session. Never a reload. */
  onUnauthorized?: () => void;
};

/** Starts the shared connection for `owner`. Returns a stop function (also releases leadership). */
export function startEvents(
  owner: string,
  onEvent: (e: HubEvent) => void,
  deps: HubDeps = browserDeps(),
  status?: (s: { leader: boolean }) => void,
  fence: HubFence = {},
): () => void {
  const d = deps;
  const tag = fence.tag ?? null;
  let readySeen = false;
  let probing = false;
  let stopped = false;
  let leader = false;
  let source: EventSource | null = null;
  let lastId = 0;
  let lastBeat = d.now();
  let attempt = 0;
  let reconnect: ReturnType<typeof setTimeout> | undefined;
  let release: (() => void) | undefined;
  let pendingReq: AbortController | null = null;
  const seen: number[] = [];
  const seenSet = new Set<number>();
  const bc = d.BroadcastChannel ? new d.BroadcastChannel(CHANNEL) : null;

  const dedupe = (id: number): boolean => {
    if (seenSet.has(id)) return false;
    seenSet.add(id);
    seen.push(id);
    if (seen.length > 1024) seenSet.delete(seen.shift()!);
    lastId = Math.max(lastId, id);
    return true;
  };
  // "ready" and heartbeats carry the server's cursor: resume from it even if no update was seen yet.
  const noteCursor = (name: string, data: any) => {
    if ((name === 'ready' || name === 'heartbeat') && typeof data?.lastEventId === 'number') lastId = Math.max(lastId, data.lastEventId);
  };
  const apply = (e: HubEvent) => {
    if (e.id !== undefined && !dedupe(e.id)) return;
    onEvent(e);
  };

  if (bc) {
    bc.onmessage = (m: MessageEvent<Relay>) => {
      const msg = m.data;
      if (!msg || msg.owner !== owner) return; // another account's tab: never apply
      if ((msg.tag ?? null) !== tag) return; // same account, another session (before/after acting): never apply
      if (msg.name === 'hello') {
        // A tab just opened: tell it where the stream is, so a later hand-over resumes from the right place.
        if (leader) bc.postMessage({ owner, tag, name: 'heartbeat', data: { lastEventId: lastId } } satisfies Relay);
        return;
      }
      lastBeat = d.now();
      noteCursor(msg.name, msg.data);
      if (msg.name === 'heartbeat') return;
      if (!leader) apply({ name: msg.name, id: msg.id, data: msg.data });
    };
  }
  bc?.postMessage({ owner, tag, name: 'hello', data: {} } satisfies Relay);
  const relay = (e: HubEvent) => bc?.postMessage({ owner, tag, name: e.name, id: e.id, data: e.data } satisfies Relay);

  function closeSource() {
    if (source) {
      source.close();
      source = null;
    }
    clearTimeout(reconnect);
  }
  function connect() {
    if (stopped || !leader) return;
    closeSource();
    const q = new URLSearchParams();
    if (lastId) q.set('lastEventId', String(lastId));
    if (tag) q.set('st', tag);
    const qs = q.toString();
    const es = new d.EventSource(qs ? `/api/events?${qs}` : '/api/events');
    source = es;
    readySeen = false;
    for (const name of ['update', 'ready', 'reset', 'heartbeat'] as const) {
      es.addEventListener(name, (m) => {
        let data: any;
        try {
          data = JSON.parse((m as MessageEvent).data);
        } catch {
          return;
        }
        if (tag) {
          if (name === 'ready') {
            if (typeof data?.sessionTag === 'string' && data.sessionTag !== tag) {
              // Confirmed: the cookie now belongs to another session. Apply nothing; the caller reloads.
              closeSource();
              fence.onChanged?.();
              return;
            }
            readySeen = true;
          } else if (!readySeen) return; // nothing is applied or relayed before the stream proves its identity
        } else if (name === 'ready') readySeen = true;
        attempt = 0;
        lastBeat = d.now();
        const ev: HubEvent = { name, id: name === 'update' && typeof data?.id === 'number' ? data.id : undefined, data };
        noteCursor(name, data);
        if (name === 'heartbeat') {
          relay(ev);
          return;
        }
        relay(ev);
        apply(ev);
      });
    }
    es.onerror = () => {
      // CONNECTING: the browser retries on its own. CLOSED: an HTTP refusal (401/503); back off ourselves.
      if (es.readyState !== 2 || source !== es) return;
      closeSource();
      apply({ name: 'closed', data: {} });
      const wait = Math.min(60_000, 2000 * 2 ** Math.min(attempt++, 5));
      reconnect = setTimeout(connect, wait);
      // Ambiguous (network, 5xx, 401 from expiry, 409): one identity probe per backoff cycle (§2h).
      if (!readySeen && fence.probe && !probing) {
        probing = true;
        fence
          .probe(tag)
          .then((o) => {
            if (stopped) return;
            if (o === 'changed') fence.onChanged?.();
            else if (o === 'unauthorized') fence.onUnauthorized?.();
            // transient / ok: keep backing off, keep the page as it is
          })
          .catch(() => undefined)
          .finally(() => (probing = false));
      }
    };
  }

  function lead(): Promise<void> {
    leader = true;
    lastBeat = d.now();
    status?.({ leader: true });
    connect();
    return new Promise<void>((resolve) => {
      release = resolve;
    });
  }
  function demote() {
    if (!leader) return;
    leader = false;
    release = undefined;
    lastBeat = d.now(); // grace window: the new leader's first heartbeat arrives within 25 s
    closeSource();
    status?.({ leader: false });
  }

  function campaign(steal = false) {
    if (stopped || !d.locks) return;
    pendingReq?.abort();
    const ctrl = (pendingReq = new AbortController());
    d.locks
      .request(LOCK_NAME, steal ? { mode: 'exclusive', steal: true } : { mode: 'exclusive', signal: ctrl.signal }, lead)
      .catch(() => undefined) // AbortError: stolen from us, or our queued request was replaced
      .finally(() => {
        if (ctrl !== pendingReq) return; // a replaced request settling must not demote its successor
        const wasLeader = leader;
        demote();
        if (!stopped && wasLeader) campaign(); // queue up again behind the new leader
      });
  }

  if (!d.locks) {
    // No Web Locks: every tab is its own leader.
    leader = true;
    connect();
  } else {
    campaign();
  }
  const watchdog = d.locks
    ? setInterval(() => {
        if (leader || stopped || !d.visible()) return;
        if (d.now() - lastBeat > d.staleMs) {
          lastBeat = d.now(); // one attempt per window
          campaign(true);
        }
      }, d.checkMs)
    : undefined;

  return () => {
    stopped = true;
    clearInterval(watchdog);
    pendingReq?.abort();
    closeSource();
    release?.(); // releases the lock if we hold it
    bc?.close();
    leader = false;
  };
}
